import React, {useState, useEffect} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import SurveyResultsComponent from '../../modules/surveyanalytics/surveyanalytics'
import SurveyGaugeChart from '../../modules/surveyanalytics/SurveyGaugeChart'
import HelpVideoModal from '../../modules/utils/HelpVideoModal'
import SurveyOrderResultComponent from '../../modules/surveyanalytics/SurveyOrderResultComponent'
import SurveyImprovementReportComponent from '../../modules/surveyanalytics/SurveyImprovementReportComponent'
import SurveyTableComponent from '../../modules/surveyanalytics/surveyanalyticstable'
import {sanitizeChatGptResponse} from '../../modules/surveyanalytics/utils'
import axios from 'axios';
import Swal from 'sweetalert2'


interface SurveyOrderResult {
    rankInDepartment: number
    totalInDepartment: number
    rankInFaculty: number
    totalInFaculty: number
    rankInUniversity: number
    totalInUniversity: number
}

const API_URL = process.env.REACT_APP_API_URL;
const isAiActive = process.env.REACT_APP_YZACTIVE === '1';
const manualFeedbackInfoText = `Değerli öğretim elemanları,\n\nAnket sonuçlarına yönelik gözlemlerinizi ve iyileştirme önerilerinizi aşağıdaki alana kendi değerlendirmenize göre yazabilirsiniz.\n\nBu alanı kullanarak dersinizle ilgili güçlü ve gelişime açık yönleri not alabilir, gelecek dönem için planladığınız aksiyonları kaydedebilirsiniz.`

const SurveyResult = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const state: any = location.state
    const aiInfoText = `Değerli öğretim elemanları,\n\nDers değerlendirme anketlerinin genel olarak yüksek ya da düşük puanlara sahip olması her durumda dersin kalitesini birebir yansıtmayabilir. Bir dersin çok sevilmesi, o dersin kaliteli olduğu anlamına gelmeyebilir. Öğrenciler özellikle zorlayıcı ana derslerde düşük puan verme eğilimindedir.\n\nDers değerlendirme anketlerinin ana amacı iyileştirmeye yönelik bir gösterge olarak kullanılabilmesidir. Soruların kendi içinde kıyaslanması ile güçlü ve zayıf görünen yönler tespit edilebilir ve önlemler alınabilir. Unutulmamalıdır ki dersin daha iyi şekilde işlenmesine yönelik yapılan her hareket kaliteyi artırıcı etki yapar ve PUKÖ (Planla, Uygula, Kontrol Et, Önlem Al) döngüsünün çevrilmesine katkı sağlar.\n\nYapay zeka analiz yaparken yalnızca ilgili dersin anket verilerini dikkate alır ve kendi içinde değerlendirir. Anket sonuçları çok iyi de olsa, iyileştirilecek noktalar bulmaya odaklanır. Bu analizler sadece fikir verme amaçlıdır; nihai rapor sizin ders hakkında yaptığınız analizler ve alacağınız önlemlerle oluşmalıdır.`

    const [surveyResults, setSurveyResults] = React.useState<any[]>([])
    const [survey, setSurvey] = React.useState('')
    const [finished, setFinished] = React.useState(0)
    const [orderResult, setOrderResult] = useState<SurveyOrderResult | null>(null)
    const [chatGptAnalysis, setChatGptAnalysis] = useState<string | null>(null)
    const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false)
    const [showModal, setShowModal] = useState(false)

    const convertTextToHTML = (text: string): string => {
        const preparedText = text
            .replace(/(### .*)/g, '\n$1\n') // H3 başlıkları                     
            .replace(/(\*\*.*?\*\*):/g, '\n$1:\n');     // Kalın başlık benzeri olanları ayır

        const html = preparedText
            .split('\n')
            .map(line => {
                line = line.trim();

                if (line.startsWith('### ')) {
                    return `<h3>${line.replace('### ', '').trim()}</h3>`;
                }

                // Kalın yazılar: **kelime**
                line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

                // Eğer hâlâ ':' ile biten bir strong başlık varsa
                if (/^<strong>.*<\/strong>:/.test(line)) {
                    return `<p>${line}</p>`;
                }

                if (line.length > 0) {
                    return `<p>${line}</p>`;
                }

                return '';
            })
            .join('\n')
            .replace(/#/g, "");

        return html;
    };


    useEffect(() => {
        if (!state) {
            return
        }

        axios
            .get(`${API_URL}/Custom/GetSurveyAnalyticsResultWithRanking?semesterCourseId=${state}`)
            .then((res: any) => {
                if (res.data && res.data.length > 0) {
                    setOrderResult(res.data[0]) // öğretim üyesinin verisi
                }
            })
            .catch((error) => {
                console.error('Sıralama verileri alınırken hata oluştu:', error)
            })
    }, [state])

    useEffect(() => {
        if (!state) {
            return
        }

        axios
            .get(`${API_URL}/Custom/GetSurveyResultData?semesterCourseId=${state}`)
            .then((res: any) => {
                const {surveyJson, surveyResults: apiSurveyResults, courseOutcomes, hasCourseOutcomeData} = res.data || {}

                let parsedSurvey: any
                try {
                    parsedSurvey = typeof surveyJson === 'string' ? JSON.parse(surveyJson) : surveyJson
                } catch (error) {
                    console.error('Anket şablonu çözümlenemedi:', error)
                    setSurveyResults([])
                    setFinished(0)
                    return
                }

                if (!parsedSurvey) {
                    setSurveyResults([])
                    setFinished(0)
                    return
                }

                const resultsArray = Array.isArray(apiSurveyResults) ? apiSurveyResults : []
                const outcomeArray = Array.isArray(courseOutcomes) ? courseOutcomes : []
                const hasOutcomeData = typeof hasCourseOutcomeData === 'boolean' ? hasCourseOutcomeData : outcomeArray.length > 0

                if (!hasOutcomeData) {
                    setSurvey(parsedSurvey)
                    setSurveyResults(resultsArray)
                    setFinished(1)

                    Swal.fire({
                        icon: 'info',
                        title: 'Ders Öğrenme Çıktıları Tanımlanmamış',
                        width: '60em',
                        html: `
                                    Bu derse ait <strong>ders öğrenme çıktıları</strong> bilgi paketine <strong>dönem başlamadan önce</strong> girilmemiştir.<br/><br/>
                                    Bu nedenle bu derste <strong>öğrencilere yalnızca sabit sorular</strong> yöneltilmiş, öğrenme çıktıları ile ilgili değerlendirme yapılamamıştır.<br/><br/>
                                    Lütfen gelecekteki dönemler için bilgi paketi girişlerini zamanında tanımlayınız.
                                `,
                        confirmButtonText: 'Tamam',
                    })

                    return
                }

                const newElements = outcomeArray.map((co: any) => ({
                    type: 'rating',
                    name: 'oc_' + co.id.toString(),
                    title: co.name,
                    rateCount: 10,
                    rateMax: 10,
                }))

                if (parsedSurvey.pages && parsedSurvey.pages.length > 0) {
                    parsedSurvey.pages[0].elements = [
                        ...(parsedSurvey.pages[0].elements || []),
                        ...newElements,
                    ]
                }

                const matrixQuestionNames = parsedSurvey.pages
                    ?.flatMap((page: any) => page.elements)
                    ?.filter((el: any) => el.type === 'matrix')
                    ?.map((el: any) => el.name) || []

                const filteredSurveyResults = resultsArray.filter((entry: any) => {
                    const isValid = matrixQuestionNames.every((qname: any) => {
                        const answers = entry[qname]
                        return answers && typeof answers === 'object' && Object.keys(answers).length >= 2
                    })
                    return isValid
                })

                setSurvey(parsedSurvey)
                setSurveyResults(filteredSurveyResults)
                setFinished(1)
            })
            .catch((error) => {
                console.error('Anket sonuçları alınırken hata oluştu:', error)
                setSurveyResults([])
                setFinished(0)
            })
    }, [state])


    useEffect(() => {
        if (!state) return

        if (!isAiActive) {
            setIsLoadingAnalysis(false)
            setChatGptAnalysis(null)
            return
        }

        setIsLoadingAnalysis(true)

        axios.post(`${API_URL}/Custom/RunSurveyChatGptAnalysis?semesterCourseId=${state}`)
            .then((res: any) => {
                const parsedAnalysis = sanitizeChatGptResponse(res?.data?.response)
                setChatGptAnalysis(parsedAnalysis)
            })
            .catch((err) => {
                console.error("ChatGPT analizi yapılamadı:", err)
                setChatGptAnalysis(null)
            })
            .finally(() => setIsLoadingAnalysis(false))
    }, [state, isAiActive])




    return (
        <>  <div className="d-flex justify-content-between align-items-center mb-4">
            <button className="btn btn-light" onClick={() => navigate('/dashboard')}>
                🔙 Geri
            </button>

            <HelpVideoModal videoName="DersDegerlendirmeAnlatimVideosu" />
        </div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                    {isAiActive ? '🧠 Yapay Zeka Analizi & İyileştirme Raporu' : '🛠 İyileştirme Raporu'}
                </button>
            </div>
            {showModal && (
                <div className="modal show d-block" tabIndex={-1} role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{isAiActive ? '📊 Yapay Zeka Analizi & İyileştirme Raporu' : '🛠 İyileştirme Raporu'}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {isAiActive ? (
                                    <div className="alert alert-info" role="alert" style={{ whiteSpace: 'pre-line' }}>
                                        {aiInfoText}
                                    </div>
                                ) : (
                                    <div className="alert alert-info" role="alert" style={{ whiteSpace: 'pre-line' }}>
                                        {manualFeedbackInfoText}
                                    </div>
                                )}
                                {isAiActive && (
                                    <div className="mb-5">
                                        <h6>ChatGPT Analizi</h6>
                                        {isLoadingAnalysis ? (
                                            <p>Yapay zeka analizi hazırlanıyor. Bu işlem bir kaç dakika sürebilir...</p>
                                        ) : (
                                            <>
                                                {chatGptAnalysis ? (
                                                    <div
                                                        className="mt-3"
                                                        dangerouslySetInnerHTML={{
                                                            __html: convertTextToHTML(chatGptAnalysis),
                                                        }}
                                                    />
                                                ) : (
                                                    <p>Analiz mevcut değil veya yapılamadı. Analiz ve rapor işlemleri için lütfen anketin sonlanmasını bekleyiniz.</p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                                {finished === 1 && (
                                    <SurveyImprovementReportComponent semesterCourseId={state} />
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Kapat</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {finished === 1 && surveyResults && <SurveyOrderResultComponent result={orderResult} />}
            {finished === 1 && surveyResults && <SurveyGaugeChart surveyJson={survey} data={surveyResults} />}
            {finished === 1 && surveyResults && <SurveyResultsComponent surveyJson={survey} surveyData={surveyResults} />}
            {finished === 1 && surveyResults && <SurveyTableComponent surveyJson={survey} surveyData={surveyResults} />}
            {/* 🧠 ChatGPT Analiz Bölümü */}

        </>
    )
}


export default SurveyResult
